import type {
    SupplierDetailsItem,
    SupplierDetailsResponse,
    SupplierPaymentItem,
    SupplierPaymentsResponse,
    SupplierStatementResponse,
    SupplierStatementRow,
} from '@/api/models';
import { tetriFromDecimal } from '@/utils/money';

/**
 * Wire → domain for supplier money (BOS-162).
 *
 * The supplier endpoints predate this app's integer-tetri contract and still
 * send decimal GEL. Every amount gains a `*Tetri` twin here, at the one edge
 * that crosses, so the pages downstream do arithmetic and formatting on
 * integers. The decimal fields stay on the type as the wire's own echo — they
 * are what the server said, they are what a network log shows — but nothing
 * should compute on them.
 *
 * `tetriFromDecimal` parses digit positions rather than multiplying by 100:
 * `50.07 * 100` is `5006.999…` in binary floating point, and a supplier balance
 * that loses a tetri is a supplier balance that stops matching the ledger.
 */

export const toSupplierDetailsItem = (
    wire: SupplierDetailsItem,
): SupplierDetailsItem => ({
    ...wire,
    balanceTetri: tetriFromDecimal(wire.balance),
    owedTotalTetri: tetriFromDecimal(wire.owedTotal),
    paidTotalTetri: tetriFromDecimal(wire.paidTotal),
    unallocatedTotalTetri: tetriFromDecimal(wire.unallocatedTotal ?? 0),
});

export const toSupplierDetailsResponse = (
    wire: SupplierDetailsResponse,
): SupplierDetailsResponse => ({
    ...wire,
    items: (wire.items ?? []).map(toSupplierDetailsItem),
});

export const toSupplierPaymentItem = (
    wire: SupplierPaymentItem,
): SupplierPaymentItem => ({
    ...wire,
    amountTetri: tetriFromDecimal(wire.amount),
});

export const toSupplierPaymentsResponse = (
    wire: SupplierPaymentsResponse,
): SupplierPaymentsResponse => ({
    ...wire,
    items: (wire.items ?? []).map(toSupplierPaymentItem),
});

const toStatementRow = (wire: SupplierStatementRow): SupplierStatementRow => ({
    ...wire,
    amountTetri: tetriFromDecimal(wire.amount),
    runningBalanceTetri: tetriFromDecimal(wire.runningBalance),
});

export const toSupplierStatementResponse = (
    wire: SupplierStatementResponse,
): SupplierStatementResponse => ({
    ...wire,
    openingBalanceTetri: tetriFromDecimal(wire.openingBalance),
    closingBalanceTetri: tetriFromDecimal(wire.closingBalance),
    owedTotalTetri: tetriFromDecimal(wire.owedTotal),
    paidTotalTetri: tetriFromDecimal(wire.paidTotal),
    rows: (wire.rows ?? []).map(toStatementRow),
});
